
import Vue from 'vue'
import { Company } from '@/types/Company'
import axios, { AxiosError } from 'axios'
import { EventBus } from '@/plugins/eventBus'
import { state } from '@/plugins/state'
import { VueConstructor } from 'vue/types/umd'
import CompanyAutocomplete from './CompanyAutocomplete.vue'
import SelectSpace from '../SelectSpace.vue'

type CompanyWithIndex = Company & { [key: string]: unknown }

// Helper function for deep cloning (uses structuredClone if available, otherwise JSON methods)
function deepClone<T>(obj: T): T {
    return (globalThis as any).structuredClone(obj)
}

// Helper function to process addresses (used in data() before methods are available)
function checkAddressHelper(sourceCompany: CompanyWithIndex): CompanyWithIndex {
  // Process addresses to remove zipcodes if they exist
  if (sourceCompany.address) {
    const zipcodeRegex = /(?<!^)\s\d{5}\s/
    const match = sourceCompany.address.match(zipcodeRegex)
    if (match) {
      sourceCompany.address = sourceCompany.address.substring(0, match.index).trim()
    }
  }
  
  if (sourceCompany.head_office_address) {
    const zipcodeRegex = /(?<!^)\s\d{5}\s/
    const match = sourceCompany.head_office_address.match(zipcodeRegex)
    if (match) {
      sourceCompany.head_office_address = sourceCompany.head_office_address.substring(0, match.index).trim()
    }
  }
  
  if (sourceCompany.billing_address) {
    const zipcodeRegex = /(?<!^)\s\d{5}\s/
    const match = sourceCompany.billing_address.match(zipcodeRegex)
    if (match) {
      sourceCompany.billing_address = sourceCompany.billing_address.substring(0, match.index).trim()
    }
  }
  
  return sourceCompany
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof CompanyAutocomplete>>).extend({
  name: 'CompanyForm',
  props: {
    isUpdate: {
      type: Boolean,
      required: false,
      default: false
    },
    company: {
      type: Object as () => Company | null,
      required: false,
      default: null
    }
  },
  components: {
    CompanyAutocomplete,
    SelectSpace
  },
  data(): { 
    localCompany: CompanyWithIndex
    apeCodeSearch: string
    spaceId: number | null | undefined
  } {
    // Initialize from provided company or create a blank template
    let sourceCompany = this.company
      ? deepClone(this.company) as CompanyWithIndex
      : {
          name: '',
          siren: '',
          trade_name: '',
          complete_name: '',
          state: undefined,
          siret: '',
          category: undefined,
          legal_form: undefined,
          ape_code: undefined,
          activity_details: '',
          prefix: '',
          external_ref: '',
          address: '',
          postal_code: '',
          city: '',
          country: '',
          head_office_address: '',
          billing_address: '',
          phone: '',
          phone_unformatted: '',
          website: '',
          intra_community_vat_code: '',
          code_idcc: '',
          code_idcc_label: '',
          annual_revenue_k: undefined,
          employee_count: undefined,
          etp_count: undefined,
          is_foreign: false,
          is_group: false,
          projects: '',
          flexibility_management: '',
          comment: '',
          parent_id: undefined,
          managers: []
        } as CompanyWithIndex
    
    // Set trade_name to company name if it doesn't exist or is empty
    if (!sourceCompany.trade_name || sourceCompany.trade_name.trim() === '') {
      sourceCompany.trade_name = sourceCompany.name || ''
    }
    
    sourceCompany = checkAddressHelper(sourceCompany)
    
    // Initialize spaceId: use state agency if available, otherwise undefined (user must select)
    let initialSpaceId = state.agency ? Number.parseInt(state.agency) : undefined
    if (this.isUpdate) {
      const companySpaceId = this.company?.space_id
      if (companySpaceId !== null && companySpaceId !== undefined) {
        const parsedSpaceId = Number(companySpaceId)
        initialSpaceId = Number.isNaN(parsedSpaceId) ? undefined : parsedSpaceId
      } else {
        initialSpaceId = undefined
      }
    }
    
    return {
      localCompany: sourceCompany,
      apeCodeSearch: '',
      spaceId: initialSpaceId,
    }
  },
  mounted() {
    if (this.isUpdate) {
      const companySpaceId = this.company?.space_id
      if (companySpaceId !== undefined && companySpaceId !== null) {
        const parsedSpaceId = Number(companySpaceId)
        if (!Number.isNaN(parsedSpaceId)) {
          this.spaceId = parsedSpaceId
        }
      }
    }
  },
  computed: {
    companyStates(): Array<{ text: string; value: string }> {
      return [
        {text: 'prospect', value: 'prospect'},
        {text: 'active_client', value: 'active_client'},
        {text: 'inactive_client', value: 'inactive_client'},
        {text: 'suspect', value: 'suspect'}
      ]
    },
    companyCategories(): string[] {
      return ['TPE', 'PME', 'ETI', 'GE']
    },
    legalForms(): Array<{ text: string; value: string }> {
      // Access the legal_form_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.legal_form_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: description as string
      })).sort((a, b) => a.text.localeCompare(b.text))
    },
    apeNafOptions(): Array<{ text: string; value: string }> {
      // Access the ape_naf_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.ape_naf_codes
      
      // Only keep codes that match the valid search pattern:
      // pattern matches APE/NAF codes in format: 2 digits . 2 digits and 1 uppercase letter (e.g., '47.51Z')
      const validCodeRegex = /^\d{2}\.\d{2}[A-Z]$/
      
      return Object.entries(codes)
        .filter(([code]) => validCodeRegex.test(code))
        .map(([code, description]) => ({
          value: code,
          text: `${description} (${code})`
        }))
        .sort((a, b) => a.value.localeCompare(b.value))
    },
    employeeCountOptions(): Array<{ text: string; value: string }> {
      // Access the employee_count_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.employee_count_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: description as string
      })).sort((a, b) => {
        // Sort by the numeric value of the code (e.g., "00", "01", "02", etc.)
        // Handle special case "NN" (non-employer) which should come first
        if (a.value === 'NN') return -1
        if (b.value === 'NN') return 1
        return a.value.localeCompare(b.value)
      })
    },
    requiredRules(): (v: string) => boolean | string {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.required_field')
    },
    sirenRules(): (v: string) => boolean | string {
      return (v: string) => !v || v.length === 9 || this.$vuetify.lang.t('$vuetify.siren_must_be_9_digits')
    },
    siretRules(): (v: string) => boolean | string {
      return (v: string) => !v || v.length === 14 || this.$vuetify.lang.t('$vuetify.siret_must_be_14_digits')
    },
    addressRules(): (v: string) => boolean | string {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.address_required')
    },
    postalCodeRules(): (v: string) => boolean | string {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.postal_code_required')
    },
    cityRules(): (v: string) => boolean | string {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.city_required')
    },
    extractedAddress: {
      get(): string {
        if (!this.localCompany.address) return ''
        const zipcodeRegex = /(?<!^)\s\d{5}\s/
        const match = this.localCompany.address.match(zipcodeRegex)
        if (match) {
          return this.localCompany.address.substring(0, match.index).trim()
        }
        return this.localCompany.address
      },
      set(value: string) {
        this.localCompany.address = value
      }
    },
    extractedHeadOfficeAddress(): string {
      if (!this.localCompany.head_office_address) return ''
      // Match 5 digit zipcode pattern that is not at the start of the string
      const zipcodeRegex = /(?<!^)\s\d{5}\s/
      const match = this.localCompany.head_office_address.match(zipcodeRegex)
      if (match) {
        return this.localCompany.head_office_address.substring(0, match.index).trim()
      }
      return this.localCompany.head_office_address
    },
    extractedBillingAddress(): string {
      if (!this.localCompany.billing_address) return ''
      // Match 5 digit zipcode pattern that is not at the start of the string
      const zipcodeRegex = /(?<!^)\s\d{5}\s/
      const match = this.localCompany.billing_address.match(zipcodeRegex)
      if (match) {
        return this.localCompany.billing_address.substring(0, match.index).trim()
      }
      return this.localCompany.billing_address
    }
  },
  methods: {
    removeManager(idx: number): void {
      if (this.localCompany.managers) {
        this.localCompany.managers.splice(idx, 1)
      }
    },
    updateHeadOfficeAddress(value: string): void {
      // Store the address without zipcode
      this.localCompany.head_office_address = value
    },
    updateBillingAddress(value: string): void {
      // Store the address without zipcode
      this.localCompany.billing_address = value
    },
    async saveCompany(action: 'post' | 'put'): Promise<void> {
      const form = this.$refs.form as Vue & { validate: () => boolean }
      if (form && form.validate()) {
        // Validate that a space is selected
        if (action === 'post' && !this.spaceId) {
          document.querySelector('.v-form')?.scrollIntoView({ behavior: 'smooth' })
          return
        }

        // Clean up the data before saving
        const companyData = { ...this.localCompany }
        
        // Remove display-only fields
        delete companyData.manager_name

        // Prepare the request body with space_id
        const requestBody = {
          company: {
            space_id: this.spaceId, // Use the selected space_id
            name: companyData.name,
            trade_name: companyData.trade_name ?? null,
            complete_name: companyData.complete_name ?? null,
            state: companyData.state ?? null,
            siret: companyData.siret ?? null,
            siren: companyData.siren ?? null,
            category: companyData.category ?? null,
            legal_form: companyData.legal_form ?? null,
            ape_code: companyData.ape_code ?? null,
            activity_details: companyData.activity_details ?? null,
            prefix: companyData.prefix ?? null,
            address: companyData.address ?? null,
            postal_code: companyData.postal_code ?? null,
            city: companyData.city ?? null,
            country: companyData.country ?? null,
            head_office_address: companyData.head_office_address ?? null,
            billing_address: companyData.billing_address ?? null,
            phone: companyData.phone ?? null,
            phone_unformatted: companyData.phone_unformatted ?? null,
            website: companyData.website ?? null,
            intra_community_vat_code: companyData.intra_community_vat_code ?? null,
            code_idcc: companyData.code_idcc ?? null,
            code_idcc_label: companyData.code_idcc_label ?? null,
            annual_revenue_k: companyData.annual_revenue_k ?? null,
            employee_count: companyData.employee_count ?? null,
            etp_count: companyData.etp_count ?? null,
            external_ref: companyData.external_ref ?? null,
            is_foreign: companyData.is_foreign ?? false,
            is_group: companyData.is_group ?? false,
            projects: companyData.projects ?? null,
            flexibility_management: companyData.flexibility_management ?? null,
            comment: companyData.comment ?? null,
            parent_id: companyData.parent_id ?? null
          }
        }

        let body = requestBody as any
        let url = '/v1/company'

        // adapt the url and body for the update action
        if (action === 'put') {
          url = `/v1/company/${this.company?.id}`
          body = body.company          
        }
        // Make API call to create/update company
        axios[action](url, body)
          .then(async (response) => {
            const companyId = response.data
            
            // Create contacts if managers exist
            if (action === 'post' && companyData.managers && companyData.managers.length > 0) {
              const contactsBody = companyData.managers
                .filter(manager => manager.prenoms || manager.nom) // Only include managers with names
                .map(manager => ({
                  first_name: manager.prenoms || null,
                  last_name: manager.nom || null,
                  company_id: companyId.toString()
                }))
              
              if (contactsBody.length > 0) {
                axios.post('/v1/contacts', contactsBody)
                .catch((contactError: AxiosError) => {
                  EventBus.$emit('snackbar', {axiosError: contactError})
                })
              }
            }
            
            // Show success message
            let message = action === 'post' ? 'company_successfully_created' : 'company_successfully_updated'
            EventBus.$emit('snackbar', {
              message: this.$vuetify.lang.t(`$vuetify.${message}`),
              color: 'success'
            })

            if (action === 'post') {
              // Emit the cleaned data for parent component
              this.$emit('save', companyData)
              // Close the modal after successful creation
              this.$emit('close')
            } else {
              this.$router.push(`/companies`)
            }
          })
          .catch((error: AxiosError) => {
            // Handle error with snackbar
            EventBus.$emit('snackbar', { axiosError: error })
          })
      } else {
        const el = document.querySelector(".v-messages.error--text:first-of-type");
        if (el) {
          el.scrollIntoView();
        } else {
          document.querySelector('.v-form')?.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }
  }
})
